import { Box, Card, CardContent, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface ChartsPanelProps {
  open: boolean;
  onClose: () => void;
  precomputedData: any;
  currentTimestamp: string;
  fuelTechColors: Record<string, string>;
  allFuelTypes: string[];
  onGeneratorClick: (duid: string) => void;
}

export const ChartsPanel = ({
  open,
  onClose,
  precomputedData,
  currentTimestamp,
  fuelTechColors,
  allFuelTypes,
  onGeneratorClick,
}: ChartsPanelProps) => {
  const theme = useTheme();

  if (!open || !precomputedData) return null;

  // Get precomputed data for current timestamp
  const currentData = precomputedData.timestampData[currentTimestamp];
  if (!currentData) return null;

  const pieChartData = currentData.pieChartData;
  const topGenerators = currentData.topGenerators;
  const timeSeriesData = currentData.timeSeries24h;

  // Get precomputed emissions data
  const currentEmissions = currentData.currentEmissions;
  const currentIntensity = currentData.currentIntensity;
  const emissionsByFuelType = currentData.emissionsByFuelType;

  // Format timestamp for display (show only time)
  const formatTime = (timestamp: string) => {
    const parts = timestamp.split(" ");
    if (parts.length === 2) {
      const time = parts[1].substring(0, 5); // HH:MM
      return time;
    }
    return timestamp;
  };

  // Sample every nth point for cleaner x-axis
  const sampledTimeSeriesData = timeSeriesData.filter(
    (_: any, index: number) => index % 12 === 0
  );

  // The emissions data is already in the timeSeries24h
  const emissionsTimeSeriesData = timeSeriesData;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        left: 16,
        bottom: 16,
        zIndex: 1000,
        width: "450px",
        overflowY: "auto",
      }}
    >
      <Card
        sx={{
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(33, 33, 33, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography variant="h6">Market Analytics</Typography>
            <IconButton size="small" onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Box>

          {/* Current Fuel Mix - Pie Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Current Fuel Mix ({currentTimestamp})
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        fuelTechColors[entry.name.replace(/ /g, "_")] ||
                        "#999999"
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} MW`}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Top 10 Generators - Bar Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Top 10 Generators
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topGenerators}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const generator = data.activePayload[0].payload;
                    onGeneratorClick(generator.duid);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={90} />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} MW`}
                />
                <Bar dataKey="output" fill="#8884d8" cursor="pointer">
                  {topGenerators.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={fuelTechColors[entry.fueltech] || "#999999"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* 24-Hour Stacked Area Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              24-Hour Generation Mix
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={sampledTimeSeriesData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} MW`}
                  labelFormatter={formatTime}
                />
                <Legend />
                {allFuelTypes.map((fuelType) => (
                  <Area
                    key={fuelType}
                    type="monotone"
                    dataKey={fuelType}
                    stackId="1"
                    stroke={fuelTechColors[fuelType] || "#999999"}
                    fill={fuelTechColors[fuelType] || "#999999"}
                    name={fuelType.replace(/_/g, " ")}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Box>

          {/* 24-Hour Total Output - Line Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              24-Hour Total Output
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={sampledTimeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} MW`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                  name="Total Output"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Current Emissions Summary */}
          <Box
            sx={{
              marginBottom: 3,
              padding: 2,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Current Emissions Snapshot
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Emissions Rate
                </Typography>
                <Typography variant="h6">
                  {currentEmissions.toFixed(2)} t CO₂e/5min
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Grid Intensity
                </Typography>
                <Typography variant="h6">
                  {currentIntensity.toFixed(0)} kg CO₂e/MWh
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Emissions by Fuel Type - Bar Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Emissions by Fuel Type
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={emissionsByFuelType.filter((d: any) => d.emissions > 0)}
                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis label={{ value: "t CO₂e/5min", angle: -90, position: "insideLeft" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} t CO₂e/5min`}
                />
                <Bar dataKey="emissions" fill="#FF5722">
                  {emissionsByFuelType.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={fuelTechColors[entry.name.replace(/ /g, "_")] || "#999999"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* 24-Hour Emissions Intensity - Line Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              24-Hour Grid Emissions Intensity
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={emissionsTimeSeriesData.filter(
                  (_: any, index: number) => index % 12 === 0
                )}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis label={{ value: "kg CO₂e/MWh", angle: -90, position: "insideLeft" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} kg CO₂e/MWh`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="intensity"
                  stroke="#FF5722"
                  strokeWidth={2}
                  dot={false}
                  name="Grid Intensity"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* 24-Hour Total Emissions - Area Chart */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              24-Hour Cumulative Emissions
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart
                data={emissionsTimeSeriesData.filter(
                  (_: any, index: number) => index % 12 === 0
                )}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis label={{ value: "t CO₂e/5min", angle: -90, position: "insideLeft" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#fff",
                    border: "1px solid #ccc",
                  }}
                  formatter={(value: any) => `${value} t CO₂e/5min`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="emissions"
                  stroke="#D32F2F"
                  fill="#FF5722"
                  fillOpacity={0.6}
                  name="Emissions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
